import {
  CardMembershipOutlined,
  ContentCopy,
  DeleteOutlined,
  EditOutlined,
  History,
  LocalOfferOutlined,
  MoreVert,
  ReceiptLongOutlined,
} from '@mui/icons-material';
import {
  IconButton,
  ListItemIcon,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';
import AdjustLoyaltyPointsDrawer from 'components/drawers/customer-loyalty-points-drawer/adjust-loyalty-points-drawer.component';
import DeleteCustomerDrawer from 'components/drawers/customers-drawer/delete-customer-drawer.component';
import EditCustomerDrawer from 'components/drawers/customers-drawer/edit-customer-drawer.component';
import { useGlobalDrawer } from 'components/drawers/global-drawer/global-drawer.hooks';
import SendOfferDrawer from 'components/drawers/send-offer-drawer/send-offer-drawer.component';
import { useUtilityDrawer } from 'components/drawers/utility-drawer/utility-drawer.hooks';
import { useHasPermission } from 'hooks/authorization.hooks';
import { t } from 'i18next';
import { bindMenu, bindTrigger } from 'material-ui-popup-state';
import { usePopupState } from 'material-ui-popup-state/hooks';
import useCopyResetPasswordLink from '../../customers.hooks';
import { ReactElement, useCallback } from 'react';
import CopyText from 'components/copy-text/copy-text.component';
import { useSnackbar } from 'notistack';
import { CustomerStatus } from 'components/customer/customer.constants';
import VerifyEmailLinkMenuItem from './verify-email-link-menu-item/verify-email-link-menu-item.component';
import BlockUnblockCustomerDrawer from 'components/drawers/block-unblock-customer-drawer/block-unblock-customer-drawer.component';
import BlockUnblockIcon, {
  useGetBlockUnblockIconLabel,
} from '../../block-unblock-icon/block-unblock-icon.component';
import AttachTransactionDrawer from 'components/drawers/attach-transaction-drawer/attach-transaction-drawer.component';
import CustomerStatusHistoryDrawer from 'components/drawers/customer-status-history-drawer/customer-status-history-drawer.component';

type MenuItemDescriptor = {
  key: string;
  show: boolean;
  render: () => ReactElement;
};

const CustomerDetailCardActions = ({ customer, onDelete = null }) => {
  const { enqueueSnackbar } = useSnackbar();
  const popupState = usePopupState({
    variant: 'popover',
    popupId: 'Customer.Menu',
  });
  const { getLabel } = useGetBlockUnblockIconLabel();

  const editUtilityDrawer = useUtilityDrawer();

  const redeemPointsUtilityDrawer = useUtilityDrawer();
  const blockUnblockUserUtilityDrawer = useUtilityDrawer();
  const sendOfferUtilityDrawer = useUtilityDrawer();
  const editTagsUtilityDrawer = useGlobalDrawer('globalCustomerTagsDrawer');

  const deleteUtilityDrawer = useUtilityDrawer();
  const attachTransactionUtilityDrawer = useUtilityDrawer<{
    customerId: string;
  }>();
  const statusHistoryUtilityDrawer = useUtilityDrawer<{
    customerId: string;
  }>();

  const handleEditDrawerOpen = () => {
    popupState.close();
    editUtilityDrawer.setDrawerData({ customerId: customer?.userId });
    editUtilityDrawer.setOpen(true);
  };

  const handleLoyaltyPointsDrawerOpen = () => {
    popupState.close();
    redeemPointsUtilityDrawer.setDrawerData({
      customerId: customer?.userId,
    });
    redeemPointsUtilityDrawer.setOpen(true);
  };

  const blockUnblockUserDrawerOpen = () => {
    popupState.close();
    blockUnblockUserUtilityDrawer.setDrawerData({
      customerId: customer?.userId,
      email: customer?.email,
      blocked: customer?.blocked,
    });
    blockUnblockUserUtilityDrawer.setOpen(true);
  };

  const handleSendOfferDrawerOpen = () => {
    popupState.close();
    sendOfferUtilityDrawer.setDrawerData({
      customerId: customer?.userId,
    });
    sendOfferUtilityDrawer.setOpen(true);
  };

  const handleAddTagsDrawerOpen = () => {
    popupState.close();
    editTagsUtilityDrawer.open({
      customerId: customer?.userId,
    });
  };

  const handleDeleteDrawerOpen = () => {
    popupState.close();
    deleteUtilityDrawer.setDrawerData({
      customerId: customer?.userId,
      email: customer?.email,
    });
    deleteUtilityDrawer.setOpen(true);
  };

  const handleAttachTransactionDrawerOpen = () => {
    popupState.close();
    attachTransactionUtilityDrawer.setDrawerData({
      customerId: customer?.userId,
    });
    attachTransactionUtilityDrawer.setOpen(true);
  };

  const handleStatusHistoryDrawerOpen = () => {
    popupState.close();
    statusHistoryUtilityDrawer.setDrawerData({
      customerId: customer?.userId,
    });
    statusHistoryUtilityDrawer.setOpen(true);
  };

  const handleCopyResetPasswordLink = useCopyResetPasswordLink();

  const onCopyPasswordLinkClick = useCallback(
    async email => {
      const fallbackContent = await handleCopyResetPasswordLink(email);
      if (fallbackContent) {
        enqueueSnackbar(<CopyText>{fallbackContent}</CopyText>, {
          severity: 'info',
        });
      }
    },
    [enqueueSnackbar, handleCopyResetPasswordLink],
  );

  const hasDeleteCustomerPermission = useHasPermission('DELETE_CUSTOMER');
  const hasAttachTransactionPermission = useHasPermission(
    'ATTACH_TRANSACTION_TO_USER',
  );
  const hasEditCustomerPermission = useHasPermission('EDIT_CUSTOMER');

  const menuItems: MenuItemDescriptor[] = [
    {
      key: 'editCustomer',
      show: hasEditCustomerPermission,
      render: () => (
        <MenuItem key="editCustomer" onClick={handleEditDrawerOpen}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          {t('page.customers.card.editCustomer')}
        </MenuItem>
      ),
    },
    {
      key: 'adjustLoyaltyPoints',
      show: hasEditCustomerPermission,
      render: () => (
        <MenuItem
          key="adjustLoyaltyPoints"
          onClick={handleLoyaltyPointsDrawerOpen}
        >
          <ListItemIcon>
            <CardMembershipOutlined fontSize="small" />
          </ListItemIcon>
          {t('page.customers.card.adjustLoyaltyPoints')}
        </MenuItem>
      ),
    },
    {
      key: 'blockUnblockCustomer',
      show: hasEditCustomerPermission,
      render: () => (
        <MenuItem
          key="blockUnblockCustomer"
          onClick={blockUnblockUserDrawerOpen}
        >
          <ListItemIcon>
            <BlockUnblockIcon blocked={!!customer?.blocked} fontSize="small" />
          </ListItemIcon>
          {getLabel(!!customer?.blocked)}
        </MenuItem>
      ),
    },
    {
      key: 'sendOffer',
      show: hasEditCustomerPermission,
      render: () => (
        <MenuItem key="sendOffer" onClick={handleSendOfferDrawerOpen}>
          <ListItemIcon>
            <LocalOfferOutlined fontSize="small" />
          </ListItemIcon>
          {t('page.customers.card.sendOffer')}
        </MenuItem>
      ),
    },
    {
      key: 'verifyEmail',
      show:
        hasEditCustomerPermission &&
        customer?.status === CustomerStatus.VERIFICATION_PENDING,
      render: () => (
        <VerifyEmailLinkMenuItem
          key="verifyEmail"
          popupState={popupState}
          userId={customer.userId}
        />
      ),
    },
    {
      key: 'editTags',
      show: hasEditCustomerPermission,
      render: () => (
        <MenuItem key="editTags" onClick={handleAddTagsDrawerOpen}>
          <ListItemIcon>
            <EditOutlined fontSize="small" />
          </ListItemIcon>
          {t('page.customers.card.editTags')}
        </MenuItem>
      ),
    },
    {
      key: 'copyResetPasswordLink',
      show: hasEditCustomerPermission,
      render: () => (
        <MenuItem
          key="copyResetPasswordLink"
          onClick={() => onCopyPasswordLinkClick(customer?.email)}
        >
          <ListItemIcon>
            <ContentCopy fontSize="small" />
          </ListItemIcon>
          {t('page.customers.copyResetPasswordLink.title')}
        </MenuItem>
      ),
    },
    {
      key: 'attachTransaction',
      show: hasAttachTransactionPermission,
      render: () => (
        <MenuItem
          key="attachTransaction"
          onClick={handleAttachTransactionDrawerOpen}
        >
          <ListItemIcon>
            <ReceiptLongOutlined fontSize="small" />
          </ListItemIcon>
          {t('page.customers.card.attachTransaction')}
        </MenuItem>
      ),
    },
    {
      key: 'statusHistory',
      show: true,
      render: () => (
        <MenuItem key="statusHistory" onClick={handleStatusHistoryDrawerOpen}>
          <ListItemIcon>
            <History fontSize="small" />
          </ListItemIcon>
          {t('page.customers.card.statusHistory')}
        </MenuItem>
      ),
    },
    {
      key: 'deleteCustomer',
      show: hasDeleteCustomerPermission,
      render: () => (
        <MenuItem key="deleteCustomer" onClick={handleDeleteDrawerOpen}>
          <ListItemIcon>
            <DeleteOutlined color="error" fontSize="small" />
          </ListItemIcon>
          <Typography sx={{ color: '#ef5350' }}>
            {t('page.customers.card.deleteCustomer')}
          </Typography>
        </MenuItem>
      ),
    },
  ];

  const visibleMenuItems = menuItems.filter(item => item.show);

  return (
    <>
      <IconButton aria-label="settings" {...bindTrigger(popupState)}>
        <MoreVert />
      </IconButton>
      <Menu
        {...bindMenu(popupState)}
        slotProps={{
          list: {
            // @ts-ignore
            'data-testid': 'CustomerDetailCardActions.MenuList',
          },
        }}
      >
        {visibleMenuItems.map(item => item.render())}
      </Menu>
      <EditCustomerDrawer
        open={editUtilityDrawer.open}
        close={editUtilityDrawer.close}
        drawerData={customer}
      />
      <AdjustLoyaltyPointsDrawer {...redeemPointsUtilityDrawer} />
      <SendOfferDrawer {...sendOfferUtilityDrawer} />
      <DeleteCustomerDrawer onDelete={onDelete} {...deleteUtilityDrawer} />
      <BlockUnblockCustomerDrawer {...blockUnblockUserUtilityDrawer} />
      <AttachTransactionDrawer {...attachTransactionUtilityDrawer} />
      <CustomerStatusHistoryDrawer
        open={statusHistoryUtilityDrawer.open}
        close={statusHistoryUtilityDrawer.close}
        drawerData={statusHistoryUtilityDrawer.drawerData}
      />
    </>
  );
};
export default CustomerDetailCardActions;
